import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { VocabularyEntry, VocabularyCategory } from '@/db/Types'
import {
	vocabularyKeys,
	fetchVocabulary,
	createVocabularyEntry as apiCreateEntry,
	updateVocabularyEntry as apiUpdateEntry,
	deleteVocabularyEntry as apiDeleteEntry,
	type CreateVocabularyInput,
	type UpdateVocabularyInput,
} from '@/api/queries/VocabularyQueries'

interface UseVocabularyDataReturn {
	vocabulary: VocabularyEntry[]
	vocabularyByCategory: Record<VocabularyCategory, VocabularyEntry[]>
	isLoading: boolean
	error: Error | null
	createEntry: (data: CreateVocabularyInput) => Promise<VocabularyEntry>
	updateEntry: (id: string, data: UpdateVocabularyInput) => Promise<VocabularyEntry>
	deleteEntry: (id: string) => Promise<void>
	refreshVocabulary: () => Promise<void>
}

const CATEGORIES: VocabularyCategory[] = ['formation', 'route', 'blocking', 'motion', 'protection', 'modifier']

export function useVocabularyData(playbookId: string): UseVocabularyDataReturn {
	const queryClient = useQueryClient()

	const {
		data: vocabulary = [],
		isLoading,
		error,
	} = useQuery({
		queryKey: vocabularyKeys.byPlaybook(playbookId),
		queryFn: () => fetchVocabulary(playbookId),
		enabled: !!playbookId,
	})

	const vocabularyByCategory = useMemo(() =>
		CATEGORIES.reduce((acc, cat) => {
			acc[cat] = vocabulary.filter(v => v.category === cat)
			return acc
		}, {} as Record<VocabularyCategory, VocabularyEntry[]>),
	[vocabulary])

	const createMutation = useMutation({
		mutationFn: (data: CreateVocabularyInput) => apiCreateEntry(playbookId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: vocabularyKeys.byPlaybook(playbookId) })
		},
	})

	const updateMutation = useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateVocabularyInput }) =>
			apiUpdateEntry(playbookId, id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: vocabularyKeys.byPlaybook(playbookId) })
		},
	})

	const deleteMutation = useMutation({
		mutationFn: (id: string) => apiDeleteEntry(playbookId, id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: vocabularyKeys.byPlaybook(playbookId) })
		},
	})

	async function createEntry(data: CreateVocabularyInput): Promise<VocabularyEntry> {
		return createMutation.mutateAsync(data)
	}

	async function updateEntry(id: string, data: UpdateVocabularyInput): Promise<VocabularyEntry> {
		return updateMutation.mutateAsync({ id, data })
	}

	async function deleteEntry(id: string): Promise<void> {
		return deleteMutation.mutateAsync(id)
	}

	async function refreshVocabulary(): Promise<void> {
		await queryClient.invalidateQueries({ queryKey: vocabularyKeys.byPlaybook(playbookId) })
	}

	return {
		vocabulary,
		vocabularyByCategory,
		isLoading,
		error,
		createEntry,
		updateEntry,
		deleteEntry,
		refreshVocabulary,
	}
}
